import { useState, useMemo, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import styles from './ChipBuilder.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function formatDateDisplay(isoDate) {
  if (!isoDate) return isoDate;
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// ChipBuilder
//
// Props:
//   chips          — array of active chip objects: { field, label, value, display }
//   onChipsChange  — setState setter (or updater fn) for chips array
//   fields         — array of field definitions:
//                    { key, label, type: 'text'|'select'|'date', placeholder?, options? }
//                    When options is null the field is treated as dynamic — its
//                    resolved options come from dynamicOptions[field.key].
//   dynamicOptions — { [fieldKey]: [{ value, label }] } for async-loaded option
//                    lists. Looked up when a field definition has options: null.
// ---------------------------------------------------------------------------

export default function ChipBuilder({ chips, onChipsChange, fields, dynamicOptions = {} }) {
  const [selectedField, setSelectedField] = useState(fields[0]?.key ?? '');
  const [textValue, setTextValue] = useState('');
  const textInputRef = useRef(null);

  const fieldDef = fields.find((f) => f.key === selectedField);

  // Resolve options: use dynamicOptions when the definition declares options: null
  const resolvedOptions = useMemo(() => {
    if (!fieldDef) return [];
    if (fieldDef.options === null) {
      return dynamicOptions[fieldDef.key] || [];
    }
    return fieldDef.options || [];
  }, [fieldDef, dynamicOptions]);

  const isSelectField = fieldDef?.type === 'select';
  const isDateField   = fieldDef?.type === 'date';

  const handleFieldChange = (e) => {
    setSelectedField(e.target.value);
    setTextValue('');
  };

  const addChip = useCallback(
    (field, value, displayLabel) => {
      if (!value || !value.trim()) return;
      const def = fields.find((f) => f.key === field);
      const display =
        displayLabel ||
        (def?.type === 'date' ? formatDateDisplay(value) : value);
      const newChip = { field, label: def?.label || field, value: value.trim(), display };
      // Replace any existing chip for the same field
      onChipsChange((prev) => {
        const filtered = prev.filter((c) => c.field !== field);
        return [...filtered, newChip];
      });
      setTextValue('');
    },
    [fields, onChipsChange]
  );

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter') {
      addChip(selectedField, textValue);
    }
  };

  const handleSelectValueChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const opt = resolvedOptions.find((o) => o.value === val);
    addChip(selectedField, val, opt?.label);
  };

  const handleAddClick = () => {
    if (!isSelectField) {
      addChip(selectedField, textValue);
    }
  };

  const removeChip = (field) => {
    onChipsChange((prev) => prev.filter((c) => c.field !== field));
  };

  const clearAll = () => onChipsChange([]);

  // Current value of existing chip for this field (for select default)
  const existingChip = chips.find((c) => c.field === selectedField);

  return (
    <div className={styles.chipBuilder}>
      {/* Builder row */}
      <div className={styles.chipBuilderRow}>
        <select
          className={styles.chipFieldSelect}
          value={selectedField}
          onChange={handleFieldChange}
          aria-label="Search by field"
        >
          <option value="" disabled>Search by…</option>
          {fields.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>

        {isSelectField ? (
          <select
            className={styles.chipValueSelect}
            value={existingChip?.value || ''}
            onChange={handleSelectValueChange}
            aria-label={`Value for ${fieldDef?.label}`}
          >
            <option value="">Select…</option>
            {resolvedOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : isDateField ? (
          <input
            type="date"
            className={styles.chipValueInput}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={handleTextKeyDown}
            aria-label="Date"
          />
        ) : (
          <input
            ref={textInputRef}
            type="text"
            className={styles.chipValueInput}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={handleTextKeyDown}
            placeholder={fieldDef?.placeholder || 'Value…'}
            aria-label={`Value for ${fieldDef?.label}`}
          />
        )}

        {!isSelectField && (
          <button
            type="button"
            className={styles.chipAddBtn}
            onClick={handleAddClick}
            aria-label="Add filter chip"
          >
            + Add
          </button>
        )}
      </div>

      {/* Chips row */}
      {chips.length > 0 ? (
        <div className={styles.chipsRow}>
          {chips.map((chip) => (
            <span key={chip.field} className={styles.chip}>
              <span className={styles.chipLabel}>{chip.label}:</span>
              <span className={styles.chipValue}>{chip.display}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeChip(chip.field)}
                aria-label={`Remove ${chip.label} filter`}
              >
                <X size={12} strokeWidth={2} />
              </button>
            </span>
          ))}
          <button
            type="button"
            className={styles.chipClearAll}
            onClick={clearAll}
          >
            Clear all
          </button>
        </div>
      ) : (
        <p className={styles.chipEmptyNote}>
          No active filters — showing all records.
        </p>
      )}
    </div>
  );
}
